/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingExposure.module.scss';

type Translate = (key: string) => string;

type ChainItem = {
  tag?: string;
  title: string;
  description: string;
  key?: boolean;
};

const getChain = (t: Translate): ChainItem[] => [
  {
    title: t('Primary adjustment'),
    description: t('Taxable profit increases where the examiner sits.'),
  },
  {
    title: t('Double taxation'),
    description: t('Relief runs through mutual agreement procedure and takes years.'),
  },
  {
    title: t('Interest'),
    description: t('Accrues from the original filing date, not from the assessment.'),
  },
  {
    tag: t('The one you control'),
    title: t('Penalties'),
    description: t('Usually turn on whether documentation was adequate and contemporaneous.'),
    key: true,
  },
];

export const TransferPricingExposure = () => {
  const { t } = useInt();
  const chain = getChain(t);
  const headRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const footerRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const revealables = [headRef.current, ...itemRefs.current, footerRef.current].filter(
      (el): el is HTMLDivElement | HTMLParagraphElement => Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="#F6F7F9" className={styles.section}>
      <div className={styles.head} ref={headRef}>
        <TitleBlock align="left" className={styles.titleBlock} title={t('What a rejected set costs.')} />
      </div>

      <div className={styles.chain}>
        {chain.map((item, index) => (
          <div
            key={item.title}
            className={cn(styles.item, item.key && styles.itemKey)}
            style={{ transitionDelay: `${index * 90}ms` }}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
          >
            <span className={styles.index}>{String(index + 1).padStart(2, '0')}</span>
            {item.tag && <span className={styles.tag}>{item.tag}</span>}
            <div className={styles.itemTitle}>{item.title}</div>
            <div className={styles.itemDescription}>{item.description}</div>
          </div>
        ))}
      </div>

      <p className={styles.footer} ref={footerRef}>
        {t('Three of those are out of your hands. The fourth is why the benchmarking file is')}{' '}
        <b>{t('what caps the downside')}</b>
        {t(', not just what defends against it.')}
      </p>
    </Section>
  );
};
